/**
 * Integration skill folder-layout builder (ENG-4502).
 *
 * Historically every integration scope landed as its own top-level skill folder:
 *
 *     .claude/skills/integration-google-sheets-skill-googlesheets-read/SKILL.md
 *     .claude/skills/integration-google-sheets-skill-googlesheets-write/SKILL.md
 *     .claude/skills/integration-google-sheets-skill-googlesheets-delete/SKILL.md
 *     .claude/skills/integration-google-sheets-skill-googlesheets-other/SKILL.md
 *
 * Confirmed via the claude-code-guide skill: Claude Code's skill discovery
 * does NOT recurse, so `integration-google-sheets/read/SKILL.md` style
 * nesting would be invisible. This module implements the compromise that
 * does work:
 *
 *     .claude/skills/integration-google-sheets/
 *       SKILL.md           ← single umbrella Claude sees; references scopes
 *       scopes/read.md     ← scope-specific reference, loaded on demand
 *       scopes/write.md
 *       scopes/delete.md
 *       scopes/other.md
 *
 * The umbrella SKILL.md is synthesized from the per-scope `skill_name`s and
 * an extracted description from each scope's frontmatter. Original scope
 * content (frontmatter and body) lands at `scopes/<slug>.md`.
 *
 * Pure functions here so the layout logic can be unit-tested without
 * involving the filesystem.
 */

import type { CapabilitySkillFile } from '@augmented/core';

export interface IntegrationSkillInput {
  /**
   * Wire-contract field — matches the `plugin_slug` key in the
   * /host/refresh payload. Renaming the field would break the host adapter
   * contract; the type alias here keeps the rename surface bounded.
   */
  plugin_slug: string;
  skill_id: string;
  skill_name: string;
  /** Rendered scope content including YAML frontmatter. */
  content: string;
}

export interface IntegrationBundle {
  /** Slug used as the top-level folder name (`integration-<slug>`). */
  integrationSlug: string;
  /** Files relative to the integration folder root. */
  files: CapabilitySkillFile[];
  /**
   * Stable set of scope-file relative paths (e.g. `scopes/read.md`) included
   * in this bundle. Exposed for per-file hashing / migration cleanup.
   */
  scopePaths: string[];
}

/** Extract the `description:` line from a skill frontmatter block. */
export function extractDescription(content: string): string | null {
  const match = content.match(/^---\s*([\s\S]*?)---/);
  if (!match) return null;
  const frontmatter = match[1] ?? '';
  // Match either a double-quoted string with escaped quotes / backslashes, or an
  // unquoted scalar up to end-of-line. The quoted branch captures the raw inner
  // content so escapes can be normalised below.
  const descMatch = frontmatter.match(/^\s*description:\s*(?:"((?:\\.|[^"\\])*)"|([^\n]+))\s*$/m);
  if (!descMatch) return null;
  const quoted = descMatch[1];
  if (quoted !== undefined) {
    return quoted.replace(/\\(["\\])/g, '$1').trim();
  }
  return descMatch[2]?.trim() ?? null;
}

/** Sanitize a scope slug so it's safe as a filename. */
function sanitizeScopeSlug(skillId: string, integrationSlug: string): string {
  // Strip the integration slug prefix if present — `googlesheets-read` under
  // `integration-google-sheets` reads better as just `read`.
  const normalized = skillId.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
  const prefix = integrationSlug.replace(/-/g, '');
  const stripped =
    normalized.startsWith(`${prefix}-`) ? normalized.slice(prefix.length + 1) : normalized;
  return stripped.replace(/^-|-$/g, '') || normalized || 'scope';
}

/**
 * Build the umbrella integration bundle from one or more scope skills.
 *
 * Contract:
 * - The umbrella SKILL.md always exists, even for a single-scope integration,
 *   so the on-disk shape is predictable.
 * - Its frontmatter uses an integration-wide `name` derived from the slug
 *   and a `description` that concatenates scope descriptions with " ", so
 *   Claude can match any scope keyword against a single skill entry.
 * - The body lists every scope with a link to its scope doc, giving Claude
 *   a deterministic way to load the right reference on demand.
 * - Scope content is written verbatim under `scopes/<slug>.md` — including
 *   its own frontmatter — so an agent following a link sees the full
 *   original doc.
 */
export function buildIntegrationBundle(skills: IntegrationSkillInput[]): IntegrationBundle {
  if (skills.length === 0) {
    throw new Error('buildIntegrationBundle: empty skills list');
  }
  const integrationSlug = skills[0]!.plugin_slug;
  // Deterministic scope order so the bundle content (and its hash) doesn't
  // flap when the DB returns rows in a different order between polls.
  const ordered = [...skills].sort((a, b) => a.skill_id.localeCompare(b.skill_id));

  // ENG-7006: drop EXACT-duplicate skill rows (same skill_id) before building.
  // An orphan / double install row (e.g. the ENG-6667 Origami de-dup that left a
  // second `skill-origami:agent` row behind) otherwise produces two entries that
  // collide on the same scope path, which threw below and failed the integration's
  // whole skill bundle every provision cycle. Two rows with the same skill_id are
  // the same skill, so keeping one is loss-less; a GENUINE collision (different
  // skill_ids normalising to the same scope path) still falls through to the throw.
  const seenSkillIds = new Set<string>();
  const deduped = ordered.filter((s) => {
    if (seenSkillIds.has(s.skill_id)) return false;
    seenSkillIds.add(s.skill_id);
    return true;
  });

  const entries = deduped.map((s) => {
    const scopeSlug = sanitizeScopeSlug(s.skill_id, integrationSlug);
    const description = extractDescription(s.content);
    return {
      skillId: s.skill_id,
      skillName: s.skill_name,
      description,
      scopeSlug,
      scopePath: `scopes/${scopeSlug}.md`,
      content: s.content,
    };
  });

  // Two different skill_ids can normalise to the same scopeSlug (e.g. `Read`
  // and `read!`). Without a guard the later file silently overwrites the
  // earlier one at install time while the umbrella links both bullets to the
  // same doc — fail fast so the collision is visible.
  const scopePathGroups = new Map<string, typeof entries>();
  for (const entry of entries) {
    const bucket = scopePathGroups.get(entry.scopePath);
    if (bucket) bucket.push(entry);
    else scopePathGroups.set(entry.scopePath, [entry]);
  }
  for (const [scopePath, group] of scopePathGroups) {
    if (group.length > 1) {
      const conflicts = group.map((e) => `${e.skillId} (${e.skillName})`).join(', ');
      throw new Error(
        `buildIntegrationBundle: duplicate scope path '${scopePath}' for integration '${integrationSlug}' — conflicting skills: ${conflicts}`,
      );
    }
  }

  const integrationName = slugToTitle(integrationSlug);
  // Description: join scope descriptions so Claude has broad keyword coverage
  // across the integration. Fall back to joined scope names when a scope's
  // frontmatter is missing a description line (defensive — content is
  // expected to always have frontmatter, but this keeps the umbrella
  // renderable even on weird input).
  const umbrellaDescription = entries
    .map((e) => e.description ?? `Use for ${e.skillName}.`)
    .join(' ');

  const scopeList = entries
    .map((e) => {
      const label = e.skillName || slugToTitle(e.scopeSlug);
      const desc = e.description ? ` — ${e.description}` : '';
      return `- **${label}** ([${e.scopePath}](${e.scopePath}))${desc}`;
    })
    .join('\n');

  const umbrella = [
    '---',
    `name: "${integrationName}"`,
    `description: "${escapeYamlDouble(umbrellaDescription)}"`,
    '---',
    '',
    `# ${integrationName}`,
    '',
    `This skill bundles ${entries.length === 1 ? 'one scope' : `${entries.length} scopes`} for the ${integrationSlug} integration. Each scope has a dedicated reference under \`scopes/\` that you should load on demand when the user's intent maps to it.`,
    '',
    '## Scopes',
    '',
    scopeList,
    '',
    '## How to use',
    '',
    `Identify which scope matches the user's request, then read the matching file under \`scopes/\` for the exact tool names and usage details. Do not guess tool names from this umbrella file alone.`,
    '',
  ].join('\n');

  const files: CapabilitySkillFile[] = [
    { relativePath: 'SKILL.md', content: umbrella },
    ...entries.map((e) => ({
      relativePath: e.scopePath,
      content: e.content,
    })),
  ];

  return {
    integrationSlug,
    files,
    scopePaths: entries.map((e) => e.scopePath),
  };
}

/** `google-sheets` → `Google Sheets` */
function slugToTitle(slug: string): string {
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** Escape characters that would break a double-quoted YAML scalar. */
function escapeYamlDouble(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/** Group integration skills by `plugin_slug` (wire-contract field), preserving insertion order of slugs. */
export function groupSkillsByIntegration(skills: IntegrationSkillInput[]): Map<string, IntegrationSkillInput[]> {
  const map = new Map<string, IntegrationSkillInput[]>();
  for (const s of skills) {
    const bucket = map.get(s.plugin_slug);
    if (bucket) bucket.push(s);
    else map.set(s.plugin_slug, [s]);
  }
  return map;
}

/**
 * Hash the umbrella-bundle file set into a single value so the manager only
 * re-writes when something actually changed. Hashing the concatenated
 * (relativePath, content) pairs catches new scope additions, scope
 * reorderings, and content tweaks without false positives.
 */
export function bundleFingerprint(files: CapabilitySkillFile[]): string {
  // Sort so the fingerprint is order-independent — buildIntegrationBundle already
  // sorts scopes, but a defensive re-sort here protects callers that build
  // bundles differently (tests, future shortcuts).
  const sorted = [...files].sort((a, b) => a.relativePath.localeCompare(b.relativePath));
  return sorted.map((f) => `${f.relativePath}\x00${f.content}`).join('\x01');
}
